import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financePlaidService from '@/services/financePlaid';
import { FetchApiIds } from '@/types';
import { FxBankConnection } from '@/types/api/finance';
import { Paths } from '@/types/routes';
import FinanceXBankAccountsComponent from './FinanceXBankAccounts.component';

const FinanceXBankAccountsContainer = function (): JSX.Element {
  const navigate = useNavigate();
  const toast = useToastContext();
  const orgId = getActiveOrgId();
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<{
    created: number;
    updated: number;
    categorized?: { byRules: number; byAi: number };
  } | null>(null);

  const {
    data: connections = [],
    isLoading,
    refetch,
  } = useSeoFetch<FxBankConnection[]>(
    [FetchApiIds.FX_BANK_CONNECTIONS, orgId],
    () => financePlaidService.getConnections(),
    { retry: false }
  );

  const handleSync = useCallback(
    async (connectionId: string, startDate?: string, endDate?: string) => {
      setSyncingId(connectionId);
      setSyncResult(null);
      try {
        const result = await financePlaidService.syncConnection(connectionId, {
          startDate,
          endDate,
        });
        setSyncResult({
          created: result.recordsCreated,
          updated: result.recordsUpdated,
          categorized: result.categorized,
        });
        refetch();
      } catch {
        toast.showErrorFeedback('Bank sync failed. Please try again.');
      } finally {
        setSyncingId(null);
      }
    },
    [refetch]
  );

  const handleManageIntegrations = useCallback(() => {
    navigate(Paths.SETTINGS_INTEGRATIONS);
  }, [navigate]);

  return (
    <FinanceXBankAccountsComponent
      connections={connections}
      isLoading={isLoading}
      syncingId={syncingId}
      syncResult={syncResult}
      onSync={handleSync}
      onManageIntegrations={handleManageIntegrations}
    />
  );
};

export default FinanceXBankAccountsContainer;
